<template>
  <div class="join join--col">
    <header class="join join--col">
      <h2>Договор на оказание платных образовательных услуг</h2>
      <p v-if="mode === 'create' && !showNoContractsMessage">
        На данной странице вы можете подать заявку на заключение договора на платные образовательные услуги.
      </p>
    </header>

    <!-- APPROVED: договор зарегистрирован в 1С -->
    <div class="join join--col" v-if="mode === 'approved' || hasMultipleContracts">
      <div
        v-for="(item, index) in approvedContracts"
        :key="item.id ?? index"
        class="form contract-block"
      >
        <fieldset disabled>
          <h4 v-if="hasMultipleContracts" class="contract-block__title">Договор {{ index + 1 }}</h4>
          <div class="form__group">
            <t-text label="Номер договора" :model-value="item.number"/>
            <t-text label="Дата договора" :model-value="item.date"/>
            <t-text label="Платёжный код" :model-value="item.billing_code"/>
            <div class="form__status">
              <label class="label label--muted label--semibold">&nbsp;</label>
              <div class="badge badge--success badge--w-lg badge--mobile-full" v-if="item.is_paid">Оплачен</div>
              <div class="badge badge--primary badge--w-lg badge--mobile-full" v-else>Не оплачен</div>
            </div>
          </div>
          <div class="form__group">
            <t-text label="Тип договора" :model-value="item.contract_type_text"/>
          </div>
          <div class="form__group">
            <t-text label="Направление" :model-value="item.direction_text"/>
          </div>
          <div class="form__group">
            <t-text label="Профиль" :model-value="item.profile_text"/>
          </div>
        </fieldset>

        <contract-payment-methods :is-distance="item.is_distance" :qr-code="item.qr_code"/>
      </div>
    </div>

    <div v-else-if="showNoContractsMessage" class="card card--info">
      <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>Договоров пока нет</p>
    </div>

    <!-- CREATE / EDITABLE / PENDING -->
    <form v-else-if="mode" class="form" @submit.prevent="onSubmit">
      <fieldset class="form form--fieldset" :disabled="!isEditable || readonly">
        <div class="form__group">
          <t-select
            v-model="form.contest_group_id"
            name="contest_group"
            label="Направление подготовки, на которое планируете заключить договор"
            :options="store.payContestGroups"
            required
          />
        </div>

        <div class="form__group">
          <t-select
            v-model="form.profile_id"
            name="profile"
            label="Профиль направления, на который планируете заключить договор"
            :options="profileOptions"
            required
          />
        </div>

        <div class="form__group form__group--flex-start">
          <t-select
            v-for="paymentKind in store.contractPaymentTypeKinds"
            :key="paymentKind.value"
            :model-value="paymentTypeIdByKind[paymentKind.value]"
            :name="`contract_payment_types.${paymentKind.value}.payment_type`"
            :label="paymentKind.text"
            :options="filteredPaymentTypes(paymentKind.value)"
            @update:model-value="setPaymentType(paymentKind.value, $event)"
            required
          />
        </div>

        <div class="form__group form__group--flex-start">
          <t-select
            v-model="form.contract_type_id"
            name="contract_type"
            label="Тип договора"
            :options="store.contractTypes"
          />
          <t-select
            :model-value="form.customer_kind"
            name="customer_kind"
            label="Тип заказчика"
            :options="store.contractCustomerKinds"
            @update:model-value="setCustomerKind"
            required
          />
        </div>
      </fieldset>

      <fieldset
        v-for="kind in visibleInfoKinds"
        :key="kind"
        class="form form--fieldset"
        :disabled="!isEditable || readonly"
      >
        <hr>
        <div class="join join--between join--center">
          <h4>{{ infoKindText(kind) }}</h4>
          <button
            v-if="!readonly && isEditable && canFillFromApplication(kind)"
            type="button"
            class="btn btn--primary"
            @click="fillFromApplication(kind)"
          >
            Заполнить из заявления
          </button>
        </div>

        <div class="form__group">
          <t-text v-model="infos[kind].surname" :name="`contract_infos.${kind}.surname`" label="Фамилия" required/>
          <t-text v-model="infos[kind].name" :name="`contract_infos.${kind}.name`" label="Имя" required/>
          <t-text v-model="infos[kind].patronymic" :name="`contract_infos.${kind}.patronymic`" label="Отчество"/>
        </div>
        <div class="form__group">
          <t-datepicker v-model="infos[kind].birthdate" :name="`contract_infos.${kind}.birthdate`" label="Дата рождения" required/>
          <t-phone v-model="infos[kind].phone" :name="`contract_infos.${kind}.phone`" label="Телефон" required/>
          <t-text v-model="infos[kind].email" :name="`contract_infos.${kind}.email`" label="E-mail" type="email" required/>
        </div>
        <div class="form__group">
          <t-text-growing v-model="infos[kind].birth_place_address" :name="`contract_infos.${kind}.birth_place_address`" label="Место рождения по паспорту" required/>
        </div>
        <div class="form__group">
          <t-text-growing v-model="infos[kind].registration_address" :name="`contract_infos.${kind}.registration_address`" label="Адрес" required/>
        </div>
        <div class="form__group">
          <t-text v-model="infos[kind].identity_document_type" :name="`contract_infos.${kind}.identity_document_type`" label="Вид документа, удостоверяющего личность" required/>
        </div>
        <div class="form__group">
          <t-text v-model="infos[kind].identity_series" :name="`contract_infos.${kind}.identity_series`" label="Серия"/>
          <t-text v-model="infos[kind].identity_number" :name="`contract_infos.${kind}.identity_number`" label="Номер" required/>
          <t-datepicker v-model="infos[kind].identity_date" :name="`contract_infos.${kind}.identity_date`" label="Дата выдачи" required/>
        </div>
        <div class="form__group">
          <t-text-growing v-model="infos[kind].identity_contragent" :name="`contract_infos.${kind}.identity_contragent`" label="Кем выдан" required/>
        </div>
        <div class="form__group">
          <t-text v-model="infos[kind].identity_contragent_code" :name="`contract_infos.${kind}.identity_contragent_code`" label="Код подразделения"/>
          <div class="form__spacer"/>
          <div class="form__spacer"/>
        </div>
      </fieldset>

      <hr>

      <div v-if="contract" class="join join--between join--center">
        <div><strong>Статус заявки:</strong> {{ contract.mailing_status_text }}</div>
        <button
          v-if="!readonly && contract"
          type="button"
          class="btn btn--primary"
          :disabled="!canSend"
          @click="store.notifyContract(contract.id)"
        >
          Отправить заявку
        </button>
      </div>

      <div class="form__actions" v-if="!readonly">
        <button class="btn btn--primary" type="submit" :disabled="!isEditable || isSaving">
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { TText, TTextGrowing, TSelect, TDatepicker, TPhone } from '@shared/ui'
import { useFormErrors } from '@shared/lib'
import { useApplicationStore } from '@widgets/application-profile'
import { useContractMode } from './contract/useContractMode'
import { useContractForm } from './contract/useContractForm'
import { usePaymentTypeFilter } from './contract/usePaymentTypeFilter'
import { useFillFromApplication } from './contract/useFillFromApplication'
import ContractPaymentMethods from './contract/ContractPaymentMethods.vue'

const props = defineProps({
  readonly: { type: Boolean, default: false },
})

const route = useRoute()
const store = useApplicationStore()
const { setErrors } = useFormErrors()

const contracts = computed(() => store.contracts ?? [])
const contract = computed(() => contracts.value[0] ?? null)
const hasMultipleContracts = computed(() => contracts.value.length > 1)
const approvedContracts = computed(() => (
  hasMultipleContracts.value ? contracts.value : (contract.value ? [contract.value] : [])
))
const showNoContractsMessage = computed(() => props.readonly && !contract.value)

const { mode, isEditable, canSend } = useContractMode(contract)
const { form, infos, paymentTypeIdByKind, visibleInfoKinds, setCustomerKind, buildPayload } = useContractForm(contract)
const { filteredPaymentTypes, setPaymentType } = usePaymentTypeFilter(paymentTypeIdByKind, mode)
const { canFillFromApplication, fillFromApplication } = useFillFromApplication(form, infos)

const profileOptions = computed(() =>
  store.payContestGroups.find(group => group.value === form.value.contest_group_id)?.profiles ?? []
)

const infoKindText = (kind) =>
  store.contractInfoKinds.find(k => k.value === kind)?.text ?? kind

const isSaving = ref(false)

const flattenContractErrors = (errors = {}) => {
  const result = { ...errors }
  const nestedFields = ['contract_infos', 'contract_payment_types']

  nestedFields.forEach((association) => {
    result[association]?.forEach((item, index) => {
      const errorIndex = item.index ?? index
      Object.entries(item).forEach(([field, messages]) => {
        if (field !== 'index') result[`${association}.${errorIndex}.${field}`] = messages
      })
    })
    delete result[association]
  })

  return result
}

const onSubmit = async () => {
  if (!isEditable.value) return
  if (isSaving.value) return
  isSaving.value = true
  try {
    const payload = buildPayload()
    if (contract.value) await store.updateContract(contract.value.id, payload)
    else await store.createContract(payload)
  } catch (error) {
    if (error.response?.data?.errors) setErrors(flattenContractErrors(error.response.data.errors.contract))
  } finally {
    isSaving.value = false
  }
}

watch(() => store.application.id, (id) => {
  //secretary
  if (id && props.readonly) store.fetchContracts(route.params.formId, 'secretary')
  //abiturient
  else if (id) store.fetchContracts()
}, { immediate: true })
</script>
